import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import SEO from './SEO';
import {
  UserCircleIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { defaultWhitelabelSettings } from '../utils/whitelabelPdf';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function daysLeft(iso) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
}

function SubscriptionBadge({ tier, status }) {
  if (tier === 'trial') return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-accent-100 bg-opacity-15 text-accent-100">
      <ClockIcon className="w-3.5 h-3.5" /> Free Trial
    </span>
  );
  if (tier === 'quarterly' && status === 'active') return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 bg-opacity-15 text-primary-100">
      <CheckBadgeIcon className="w-3.5 h-3.5" /> Quarterly Plan
    </span>
  );
  if (tier === 'annual' && status === 'active') return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-signal-positive bg-opacity-15 text-signal-positive">
      <CheckBadgeIcon className="w-3.5 h-3.5" /> Annual Plan
    </span>
  );
  if (tier === 'custom') return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-purple-900 bg-opacity-40 text-purple-300">
      <CheckBadgeIcon className="w-3.5 h-3.5" /> Custom Subscription
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-bg-300 text-text-300">
      <ExclamationTriangleIcon className="w-3.5 h-3.5" /> No active plan
    </span>
  );
}

// ── Mini slideshow modal ───────────────────────────────────────────────────────
const slides = [
  { title: 'Signal-Driven Model Portfolios', body: 'Browse 50+ institutional model portfolios ranked by our Power List™. Each scored with alternative data signals for early-mover advantage.' },
  { title: 'Quantitative Research Tools', body: 'Analyze ETFs and tickers with institutional factor data, sector exposures, and historical signal overlays.' },
  { title: 'AI Research Assistant', body: 'Ask anything about markets, models, or positions. Our AI understands your platform data and never hallucinates portfolio facts.' },
];

function MiniSlideshow({ onClose }) {
  const [index, setIndex] = useState(0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 px-4" onClick={onClose}>
      <div className="w-full max-w-md bg-bg-200 rounded-2xl border border-bg-300 p-8 space-y-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-text-300 font-medium uppercase tracking-widest">Platform Tour</span>
          <button onClick={onClose} className="text-text-300 hover:text-text-100 text-xl leading-none">×</button>
        </div>
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-text-100">{slides[index].title}</h3>
          <p className="text-text-200 text-sm leading-relaxed">{slides[index].body}</p>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIndex(i)} className={`rounded-full transition-all ${i === index ? 'w-5 h-2 bg-primary-100' : 'w-2 h-2 bg-bg-300'}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {index > 0 && (
              <button onClick={() => setIndex((i) => i - 1)} className="px-3 py-1.5 rounded-lg text-sm border border-bg-300 text-text-200 hover:text-text-100 transition-all">Back</button>
            )}
            {index < slides.length - 1 ? (
              <button onClick={() => setIndex((i) => i + 1)} className="px-3 py-1.5 rounded-lg text-sm bg-primary-100 text-white hover:bg-primary-200 transition-all">Next</button>
            ) : (
              <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm bg-primary-100 text-white hover:bg-primary-200 transition-all">Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, userRecord, isApproved, logout, updateProfileData, selectPaidPlan, createPortalSession } = useAuth();
  const [upgradingTo, setUpgradingTo] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: userRecord?.data?.first_name || '',
    last_name:  userRecord?.data?.last_name  || '',
    firm_name:  userRecord?.data?.firm_name  || '',
    phone:      userRecord?.data?.phone      || '',
    role:       userRecord?.data?.role       || '',
  });
  const [saving, setSaving] = useState(false);
  const [whitelabelSaving, setWhitelabelSaving] = useState(false);
  const [whitelabelSaved, setWhitelabelSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState('');
  const [logoUploadSuccess, setLogoUploadSuccess] = useState('');
  const [whitelabelDirty, setWhitelabelDirty] = useState(false);
  const [whitelabelData, setWhitelabelData] = useState({
    ...defaultWhitelabelSettings,
    firm_name: userRecord?.data?.whitelabel?.firm_name || userRecord?.data?.firm_name || '',
    ...(userRecord?.data?.whitelabel || {}),
  });

  // Sync form when userRecord arrives (async load after mount)
  useEffect(() => {
    if (userRecord?.data) {
      setFormData({
        first_name: userRecord.data.first_name || '',
        last_name:  userRecord.data.last_name  || '',
        firm_name:  userRecord.data.firm_name  || '',
        phone:      userRecord.data.phone      || '',
        role:       userRecord.data.role       || '',
      });
      if (!whitelabelDirty) {
        setWhitelabelData({
          ...defaultWhitelabelSettings,
          firm_name: userRecord.data.whitelabel?.firm_name || userRecord.data.firm_name || '',
          ...(userRecord.data.whitelabel || {}),
        });
      }
    }
  }, [userRecord, whitelabelDirty]);
  const [saved,  setSaved]  = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfileData({ ...userRecord?.data, ...formData });
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleWhitelabelSave = async () => {
    setWhitelabelSaving(true);
    try {
      await updateProfileData({
        ...userRecord?.data,
        whitelabel: whitelabelData,
      });
      setWhitelabelDirty(false);
      setWhitelabelSaved(true);
      setTimeout(() => setWhitelabelSaved(false), 3000);
    } catch (err) {
      console.error('Whitelabel save error:', err);
    } finally {
      setWhitelabelSaving(false);
    }
  };

  const updateWhitelabelField = (key, value) => {
    setWhitelabelDirty(true);
    setWhitelabelData((current) => ({ ...current, [key]: value }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;
    setLogoUploading(true);
    setLogoUploadError('');
    setLogoUploadSuccess('');
    try {
      const extension = file.name.split('.').pop() || 'png';
      const path = `whitelabel/${user.id}/${Date.now()}.${extension}`;
      const contentType = file.type || (extension.toLowerCase() === 'svg' ? 'image/svg+xml' : undefined);
      const { error } = await supabase.storage
        .from('media')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: true,
          contentType,
        });
      if (error) throw error;

      const { data } = supabase.storage.from('media').getPublicUrl(path);
      setWhitelabelData((current) => ({
        ...current,
        logo_url: data.publicUrl,
      }));
      setWhitelabelDirty(true);
      setLogoUploadSuccess('Logo uploaded. Click Save to persist these whitelabel settings.');
    } catch (err) {
      console.error('Logo upload error:', err);
      setLogoUploadError(err?.message || 'Logo upload failed. Check the media bucket upload policy and try again.');
    } finally {
      setLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgradingTo(planId);
    try {
      const response = await selectPaidPlan(planId, userRecord?.data ?? {});
      const checkout_url = response?.data?.checkout_url;
      if (checkout_url) window.location.href = checkout_url;
    } catch (err) {
      console.error('Upgrade error:', err);
    } finally {
      setUpgradingTo(null);
    }
  };

  const days = daysLeft(userRecord?.trial_ends_at);
  const initials = [userRecord?.data?.first_name, userRecord?.data?.last_name]
    .filter(Boolean).map((n) => n[0].toUpperCase()).join('')
    || user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div className="min-h-screen bg-bg-100 px-4 py-10">
      <SEO title="Profile" description="Manage your SharpeMetrix account and subscription." />
      {showSlideshow && <MiniSlideshow onClose={() => setShowSlideshow(false)} />}

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center text-2xl font-bold text-white shrink-0">
            {initials}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-100">
              {userRecord?.data?.first_name
                ? `${userRecord.data.first_name} ${userRecord.data.last_name || ''}`.trim()
                : user?.email}
            </h1>
            <p className="text-text-300 text-sm mt-0.5">{user?.email}</p>
            {userRecord?.data?.firm_name && (
              <p className="text-text-200 text-sm">{userRecord.data.firm_name}</p>
            )}
          </div>
        </div>

        {/* Subscription card */}
        <div className="rounded-2xl bg-bg-200 border border-bg-300 overflow-hidden">
          <div className="px-6 py-5 border-b border-bg-300 flex items-center gap-3">
            <CreditCardIcon className="w-5 h-5 text-text-300" />
            <h2 className="text-base font-semibold text-text-100">Subscription</h2>
          </div>

          <div className="px-6 py-6 space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-text-300 text-xs uppercase tracking-widest font-medium mb-1">Current plan</p>
                <SubscriptionBadge tier={userRecord?.subscription_tier} status={userRecord?.subscription_status} />
              </div>
              <div className="text-right">
                <p className="text-text-300 text-xs uppercase tracking-widest font-medium mb-1">Access</p>
                <span className={`text-sm font-medium ${isApproved ? 'text-signal-positive' : 'text-signal-negative'}`}>
                  {isApproved ? '● Active' : '● Inactive'}
                </span>
              </div>
            </div>

            {userRecord?.subscription_tier === 'trial' && days !== null && (
              <div className={`rounded-xl p-4 ${days <= 7 ? 'bg-red-500 bg-opacity-10 border border-red-500 border-opacity-20' : 'bg-accent-100 bg-opacity-5 border border-accent-100 border-opacity-20'}`}>
                <div className="flex items-start gap-3">
                  <ClockIcon className={`w-5 h-5 mt-0.5 shrink-0 ${days <= 7 ? 'text-red-400' : 'text-accent-100'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${days <= 7 ? 'text-red-400' : 'text-accent-100'}`}>
                      {days} day{days !== 1 ? 's' : ''} remaining in your trial
                    </p>
                    <p className="text-text-200 text-xs mt-1">Trial ends {formatDate(userRecord?.trial_ends_at)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Custom subscription — manually managed outside Stripe */}
            {userRecord?.subscription_tier === 'custom' ? (
              <div className="rounded-xl bg-purple-900 bg-opacity-10 border border-purple-500 border-opacity-30 px-4 py-4 space-y-1">
                <p className="text-sm font-semibold text-purple-300">Custom subscription</p>
                <p className="text-xs text-text-200">Your access is managed under a custom agreement. Contact <a href="mailto:support@sharpemetrix.com" className="underline text-purple-300">support@sharpemetrix.com</a> for any billing questions.</p>
              </div>
            ) : !['quarterly', 'annual'].includes(userRecord?.subscription_tier) || userRecord?.subscription_status !== 'active' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { id: 'quarterly', name: 'Quarterly Plan', price: '$1,500', period: '/3 months' },
                  { id: 'annual',    name: 'Annual Plan',    price: '$4,499', period: '/year', badge: 'Save 25%' },
                ].map((plan) => (
                  <div key={plan.id} className="rounded-xl bg-bg-100 border border-bg-300 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-text-100">{plan.name}</span>
                      {plan.badge && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 bg-opacity-20 text-primary-100 font-medium">{plan.badge}</span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-text-100">{plan.price}</span>
                      <span className="text-text-300 text-xs">{plan.period}</span>
                    </div>
                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={!!upgradingTo}
                      className="w-full py-2 rounded-lg text-xs font-medium bg-primary-100 text-white hover:bg-primary-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {upgradingTo === plan.id ? 'Redirecting…' : 'Upgrade now'}
                    </button>
                  </div>
                ))}
              </div>
                      ) : (
              <div className="rounded-xl bg-signal-positive bg-opacity-5 border border-signal-positive border-opacity-20 px-5 py-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-signal-positive">
                      {userRecord?.subscription_tier === 'quarterly' ? 'Quarterly Plan — billed every 3 months' : 'Annual Plan — billed every year'}
                    </p>
                    <p className="text-xs text-text-300">Active since {formatDate(userRecord?.updated_at)}</p>
                  </div>
                  <button
                    onClick={async () => { setPortalLoading(true); await createPortalSession().finally(() => setPortalLoading(false)); }}
                    disabled={portalLoading}
                    className="px-4 py-2 rounded-lg text-xs font-medium border border-bg-300 text-text-200 hover:text-text-100 hover:border-text-300 transition-all disabled:opacity-60"
                  >
                    {portalLoading ? 'Loading…' : 'Manage billing →'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Profile info card */}
        <div className="rounded-2xl bg-bg-200 border border-bg-300 overflow-hidden">
          <div className="px-6 py-5 border-b border-bg-300 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserCircleIcon className="w-5 h-5 text-text-300" />
              <h2 className="text-base font-semibold text-text-100">Profile</h2>
            </div>
            {!editing ? (
              <button onClick={() => setEditing(true)} className="text-sm text-primary-100 hover:text-primary-200 font-medium transition-colors">Edit</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="text-sm text-text-300 hover:text-text-100 transition-colors">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="text-sm text-primary-100 hover:text-primary-200 font-medium transition-colors disabled:opacity-60">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>

          <div className="px-6 py-6 space-y-4">
            {saved && (
              <div className="flex items-center gap-2 text-signal-positive text-sm bg-signal-positive bg-opacity-10 rounded-lg px-4 py-3">
                <ShieldCheckIcon className="w-4 h-4" /> Profile updated successfully
              </div>
            )}

            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { key: 'first_name', label: 'First name' },
                  { key: 'last_name',  label: 'Last name' },
                  { key: 'firm_name',  label: 'Firm name', span: true },
                  { key: 'phone',      label: 'Phone',     span: true, type: 'tel' },
                  { key: 'role',       label: 'Role',      span: true },
                ].map(({ key, label, span, type = 'text' }) => (
                  <div key={key} className={span ? 'sm:col-span-2' : ''}>
                    <label className="block text-xs font-medium text-text-300 uppercase tracking-widest mb-1.5">{label}</label>
                    <input
                      type={type}
                      value={formData[key]}
                      onChange={(e) => setFormData((f) => ({ ...f, [key]: e.target.value }))}
                      className="block w-full rounded-lg bg-bg-100 px-4 py-2.5 text-text-100 ring-1 ring-bg-300 focus:ring-2 focus:ring-primary-100 text-sm outline-none transition-all"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-y-5">
                {[
                  { label: 'First name',   value: userRecord?.data?.first_name },
                  { label: 'Last name',    value: userRecord?.data?.last_name },
                  { label: 'Email',        value: user?.email,                  span: true },
                  { label: 'Phone',        value: userRecord?.data?.phone,      span: true },
                  { label: 'Firm',         value: userRecord?.data?.firm_name,  span: true },
                  { label: 'Role',         value: userRecord?.data?.role },
                  { label: 'AUM range',    value: userRecord?.data?.aum_range },
                  { label: 'Member since', value: formatDate(userRecord?.created_at) },
                ].map(({ label, value, span }) => (
                  <div key={label} className={span ? 'col-span-2' : ''}>
                    <p className="text-xs text-text-300 uppercase tracking-widest font-medium">{label}</p>
                    <p className="text-text-100 text-sm mt-1">{value || '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Whitelabel factsheets card */}
        <div className="rounded-2xl bg-bg-200 border border-bg-300 overflow-hidden">
          <div className="px-6 py-5 border-b border-bg-300 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SparklesIcon className="w-5 h-5 text-text-300" />
              <div>
                <h2 className="text-base font-semibold text-text-100">Whitelabel Factsheets</h2>
                <p className="text-xs text-text-300 mt-0.5">Used when exporting whitelabel model PDFs.</p>
              </div>
            </div>
            <button
              onClick={handleWhitelabelSave}
              disabled={whitelabelSaving}
              className="text-sm text-primary-100 hover:text-primary-200 font-medium transition-colors disabled:opacity-60"
            >
              {whitelabelSaving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div className="px-6 py-6 space-y-5">
            {whitelabelSaved && (
              <div className="flex items-center gap-2 text-signal-positive text-sm bg-signal-positive bg-opacity-10 rounded-lg px-4 py-3">
                <ShieldCheckIcon className="w-4 h-4" /> Whitelabel settings saved
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-text-300 uppercase tracking-widest mb-1.5">Firm display name</label>
              <input
                type="text"
                value={whitelabelData.firm_name}
                onChange={(e) => updateWhitelabelField('firm_name', e.target.value)}
                placeholder={userRecord?.data?.firm_name || 'Your firm name'}
                className="block w-full rounded-lg bg-bg-100 px-4 py-2.5 text-text-100 ring-1 ring-bg-300 focus:ring-2 focus:ring-primary-100 text-sm outline-none transition-all"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-text-300 uppercase tracking-widest mb-1.5">Primary color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={whitelabelData.primary_color}
                    onChange={(e) => updateWhitelabelField('primary_color', e.target.value)}
                    className="h-10 w-14 rounded bg-bg-100 ring-1 ring-bg-300"
                  />
                  <input
                    type="text"
                    value={whitelabelData.primary_color}
                    onChange={(e) => updateWhitelabelField('primary_color', e.target.value)}
                    className="block w-full rounded-lg bg-bg-100 px-3 py-2.5 text-text-100 ring-1 ring-bg-300 focus:ring-2 focus:ring-primary-100 text-sm outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-300 uppercase tracking-widest mb-1.5">Accent color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={whitelabelData.accent_color}
                    onChange={(e) => updateWhitelabelField('accent_color', e.target.value)}
                    className="h-10 w-14 rounded bg-bg-100 ring-1 ring-bg-300"
                  />
                  <input
                    type="text"
                    value={whitelabelData.accent_color}
                    onChange={(e) => updateWhitelabelField('accent_color', e.target.value)}
                    className="block w-full rounded-lg bg-bg-100 px-3 py-2.5 text-text-100 ring-1 ring-bg-300 focus:ring-2 focus:ring-primary-100 text-sm outline-none"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-300 uppercase tracking-widest mb-1.5">Logo</label>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-bg-100 px-4 py-2.5 text-sm font-medium text-text-100 ring-1 ring-bg-300 hover:bg-bg-300">
                  {logoUploading ? 'Uploading...' : 'Upload logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    disabled={logoUploading}
                    className="hidden"
                  />
                </label>
                <input
                  type="url"
                  value={whitelabelData.logo_url}
                  onChange={(e) => updateWhitelabelField('logo_url', e.target.value)}
                  placeholder="https://..."
                  className="block flex-1 rounded-lg bg-bg-100 px-4 py-2.5 text-text-100 ring-1 ring-bg-300 focus:ring-2 focus:ring-primary-100 text-sm outline-none transition-all"
                />
              </div>
              {logoUploadError && (
                <p className="mt-2 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {logoUploadError}
                </p>
              )}
              {logoUploadSuccess && (
                <p className="mt-2 rounded-lg bg-signal-positive/10 px-3 py-2 text-xs text-signal-positive">
                  {logoUploadSuccess}
                </p>
              )}
              {whitelabelData.logo_url && (
                <div className="mt-3 rounded-lg bg-bg-100 border border-bg-300 p-3">
                  <img src={whitelabelData.logo_url} alt="Whitelabel logo preview" className="max-h-14 max-w-full object-contain" />
                </div>
              )}
              <div className="mt-3 rounded-lg border border-bg-300 bg-bg-100 p-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-text-300">Logo recommendations</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-text-300">
                  <li>Use a transparent PNG or SVG where possible.</li>
                  <li>Wide logos work best, around 4:1 to 6:1.</li>
                  <li>Recommended size: at least 600px wide and under 1MB.</li>
                  <li>Avoid white-only logos unless your primary color is dark enough for contrast.</li>
                </ul>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-300 uppercase tracking-widest mb-1.5">Default final-page text</label>
              <textarea
                value={whitelabelData.disclosure_text}
                onChange={(e) => updateWhitelabelField('disclosure_text', e.target.value)}
                rows={4}
                placeholder="Optional firm disclosure, compliance language, or advisor notes. This appears on the final page of whitelabel factsheets."
                className="block w-full rounded-lg bg-bg-100 px-4 py-2.5 text-text-100 ring-1 ring-bg-300 focus:ring-2 focus:ring-primary-100 text-sm outline-none transition-all"
              />
              <p className="mt-2 text-xs text-text-300">
                Export dialogs can override or add to this text for a specific PDF.
              </p>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="rounded-2xl bg-bg-200 border border-bg-300 overflow-hidden divide-y divide-bg-300">
          <button onClick={() => setShowSlideshow(true)} className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-bg-300 transition-colors group">
            <SparklesIcon className="w-5 h-5 text-text-300 group-hover:text-primary-100 transition-colors" />
            <div>
              <p className="text-sm font-medium text-text-100">Replay platform tour</p>
              <p className="text-xs text-text-300">3-slide overview of platform features</p>
            </div>
          </button>

          <button onClick={handleLogout} className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-bg-300 transition-colors group">
            <ArrowRightOnRectangleIcon className="w-5 h-5 text-text-300 group-hover:text-red-400 transition-colors" />
            <p className="text-sm font-medium text-text-100 group-hover:text-red-400 transition-colors">Sign out</p>
          </button>
        </div>
      </div>
    </div>
  );
}
